import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export interface BusinessProfile {
  id: string;
  user_id: string;
  business_name: string;
  business_type: string;
  business_description: string;
  website: string;
  location: string;
  services_offered: string;
  unique_differentiator: string;
  default_audience: string;
  default_tone: string;
  brand_positioning: string;
  logo_url?: string | null;
  created_at: string;
  updated_at: string;
}

export type BusinessProfileInput = Omit<BusinessProfile, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'team_id'>;

export const PROFILE_LIMITS: Record<string, number> = {
  free: 1,
  pro: 3,
  agency: 999,
};

export function useBusinessProfiles() {
  const { user } = useAuth();
  const [profiles, setProfiles] = useState<BusinessProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProfiles = useCallback(async () => {
    if (!user) {
      setProfiles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error: err } = await supabase
      .from('business_profiles')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (err) {
      setError(err.message);
    } else {
      setProfiles(data ?? []);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const createProfile = async (input: BusinessProfileInput): Promise<{ data: BusinessProfile | null; error: string | null }> => {
    if (!user) return { data: null, error: 'Not authenticated' };
    const { data, error: err } = await supabase
      .from('business_profiles')
      .insert({ ...input, user_id: user.id })
      .select()
      .maybeSingle();
    if (err) return { data: null, error: err.message };
    await fetchProfiles();
    return { data, error: null };
  };

  const updateProfile = async (id: string, input: Partial<BusinessProfileInput>): Promise<{ error: string | null }> => {
    const { error: err } = await supabase
      .from('business_profiles')
      .update({ ...input, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('user_id', user?.id ?? '');
    if (err) return { error: err.message };
    await fetchProfiles();
    return { error: null };
  };

  const deleteProfile = async (id: string): Promise<{ error: string | null }> => {
    const { error: err } = await supabase
      .from('business_profiles')
      .delete()
      .eq('id', id)
      .eq('user_id', user?.id ?? '');
    if (err) return { error: err.message };
    await fetchProfiles();
    return { error: null };
  };

  const duplicateProfile = async (profile: BusinessProfile): Promise<{ error: string | null }> => {
    const { id, user_id, created_at, updated_at, business_name, ...rest } = profile;
    return createProfile({ ...rest, business_name: `${business_name} (Copy)` }).then(r => ({ error: r.error }));
  };

  return { profiles, loading, error, refetch: fetchProfiles, createProfile, updateProfile, deleteProfile, duplicateProfile };
}
